import React from 'react'

import { ENABLED_RETRIES } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  LocalOnInvalidReply,
  LocalOnRetriesExhausted,
  LocalOnTimeout,
  PartFamily,
  WhatsAppFlow,
} from '@/app/module/campaigns/types'

import { ActionProps, ApiProps } from './part-config-props'
import ResponseConfigRetries from './response-config-retries'
import { ConfigDivider } from './response-configs-components'
import ResponseWhatsappFailure from './response-whatsapp-failure'
import {
  fromWhatsappFlowConfigs,
  toWhatsappFlowConfigs,
  WhatsappFlowConfigsState,
} from './response-whatsapp-flow-configs-utils'
import ResponseWhatsappInvalid from './response-whatsapp-invalid'
import ResponseWhatsappTimeout from './response-whatsapp-timeout'

type EnabledConfigsState = {
  onInvalidAnswer: boolean
  onRetriesExhausted: boolean
  onTimeout: boolean
}

type Props = ActionProps &
  ApiProps & {
    id: string
    item: WhatsAppFlow
    type: CampaignTemplateType

    changeHandler: (item: WhatsAppFlow) => void
  }

const ResponseWhatsappFlowConfigs: React.FC<Props> = ({
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  getImageProps,
  getVoiceProps,
  id,
  index,
  item,
  languages,
  orgId,
  personalizationList,
  senderIds,
  timezone,
  timezones,
  token,
  type,
  whatsappSenderIds,
  changeHandler,
}) => {
  const backendFlowConfigsState = React.useMemo(
    () =>
      fromWhatsappFlowConfigs({
        onInvalidAnswer: item.flow.onInvalidAnswer,
        onTimeout: item.flow.onTimeout,
        retries: item.flow.retries,
        onRetriesExhausted: item.flow.onRetriesExhausted,
      }),
    [item.flow.onInvalidAnswer, item.flow.onRetriesExhausted, item.flow.onTimeout, item.flow.retries],
  )
  const [enabledConfigs, setEnabledConfigs] = React.useState<EnabledConfigsState>(() => ({
    onInvalidAnswer: !!backendFlowConfigsState.onInvalidAnswer.enabled,
    onRetriesExhausted: !!backendFlowConfigsState.onRetriesExhausted.enabled,
    onTimeout: !!backendFlowConfigsState.onTimeout.enabled,
  }))

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset local enabled state when editing a different WhatsApp Form item
  React.useEffect(() => {
    setEnabledConfigs({
      onInvalidAnswer: !!backendFlowConfigsState.onInvalidAnswer.enabled,
      onRetriesExhausted: !!backendFlowConfigsState.onRetriesExhausted.enabled,
      onTimeout: !!backendFlowConfigsState.onTimeout.enabled,
    })
  }, [item.flow.id, item.id])

  const flowConfigsState = React.useMemo(
    () => ({
      ...backendFlowConfigsState,
      onInvalidAnswer: {
        ...backendFlowConfigsState.onInvalidAnswer,
        enabled: enabledConfigs.onInvalidAnswer || backendFlowConfigsState.onInvalidAnswer.enabled,
      },
      onRetriesExhausted: {
        ...backendFlowConfigsState.onRetriesExhausted,
        enabled: enabledConfigs.onRetriesExhausted || backendFlowConfigsState.onRetriesExhausted.enabled,
      },
      onTimeout: {
        ...backendFlowConfigsState.onTimeout,
        enabled: enabledConfigs.onTimeout || backendFlowConfigsState.onTimeout.enabled,
      },
    }),
    [backendFlowConfigsState, enabledConfigs],
  )

  const handleFlowConfigsChange = React.useCallback(
    (state: WhatsappFlowConfigsState) =>
      changeHandler({
        ...item,
        flow: {
          ...item.flow,
          ...toWhatsappFlowConfigs(state),
        },
      }),
    [item, changeHandler],
  )

  const handleInvalidChange = React.useCallback(
    (value: Partial<LocalOnInvalidReply>) => {
      const nextInvalid = { ...flowConfigsState.onInvalidAnswer, ...value }
      if ('enabled' in value) {
        setEnabledConfigs((state) => ({ ...state, onInvalidAnswer: !!value.enabled }))
      }
      const shouldUpdateRetries = 'enabled' in value
      let retries = flowConfigsState.retries
      if (shouldUpdateRetries) {
        retries = nextInvalid.enabled || flowConfigsState.onTimeout.enabled ? retries || ENABLED_RETRIES : 0
      }

      handleFlowConfigsChange({
        ...flowConfigsState,
        onInvalidAnswer: nextInvalid,
        retries,
      })
    },
    [flowConfigsState, handleFlowConfigsChange],
  )

  const handleTimeoutChange = React.useCallback(
    (value: Partial<LocalOnTimeout>) => {
      const nextTimeout = {
        ...flowConfigsState.onTimeout,
        ...value,
        timeout: 'enabled' in value || value.timeout === undefined ? flowConfigsState.onTimeout.timeout : value.timeout,
      }
      if ('enabled' in value) {
        setEnabledConfigs((state) => ({ ...state, onTimeout: !!value.enabled }))
      }
      const shouldUpdateRetries = 'enabled' in value
      let retries = flowConfigsState.retries
      if (shouldUpdateRetries) {
        retries = nextTimeout.enabled || flowConfigsState.onInvalidAnswer.enabled ? retries || ENABLED_RETRIES : 0
      }

      handleFlowConfigsChange({
        ...flowConfigsState,
        onTimeout: nextTimeout,
        retries,
      })
    },
    [flowConfigsState, handleFlowConfigsChange],
  )

  const handleRetriesChange = React.useCallback(
    ({ value }: { value: string }) =>
      handleFlowConfigsChange({
        ...flowConfigsState,
        retries: Number(value),
      }),
    [flowConfigsState, handleFlowConfigsChange],
  )

  const handleFailureChange = React.useCallback(
    (value: Partial<LocalOnRetriesExhausted>) => {
      if ('enabled' in value) {
        setEnabledConfigs((state) => ({ ...state, onRetriesExhausted: !!value.enabled }))
      }

      handleFlowConfigsChange({
        ...flowConfigsState,
        onRetriesExhausted: {
          ...flowConfigsState.onRetriesExhausted,
          ...value,
        },
      })
    },
    [flowConfigsState, handleFlowConfigsChange],
  )

  const actionProps: ActionProps = {
    countriesTimezones,
    countryCode,
    defaultLanguage,
    files,
    getImageProps,
    getVoiceProps,
    index,
    languages,
    personalizationList,
    senderIds,
    timezone,
    timezones,
    whatsappSenderIds,
  }
  const apiProps: ApiProps = {
    orgId,
    token,
  }

  const retriesEnabled = !!(flowConfigsState.onInvalidAnswer.enabled || flowConfigsState.onTimeout.enabled)

  return (
    <>
      <ConfigDivider />
      <ResponseWhatsappInvalid
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={type}
        id={id}
        info="Specify what we should do if the contact's WhatsApp Form response is invalid. Afterwards, we will wait for the contact to submit a valid response until they run out of chances."
        invalid={flowConfigsState.onInvalidAnswer}
        partFamily={PartFamily.WhatsAppFlow}
        onChange={handleInvalidChange}
      />
      <ConfigDivider />
      <ResponseWhatsappTimeout
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={type}
        id={id}
        info="Specify what we should do if the contact does not submit the WhatsApp Form within a period of time that you specify. Afterwards, we will wait for the contact to submit until they run out of chances."
        inputBeforeText="Take the above actions if the contact does not submit this WhatsApp Form after: "
        partFamily={PartFamily.WhatsAppFlow}
        timeout={flowConfigsState.onTimeout}
        onChange={handleTimeoutChange}
      />
      <ConfigDivider />
      <ResponseConfigRetries
        beforeText="Specify the number of times that we should allow the Contact to submit invalidly or time out before we take the Failure actions you specify below:"
        itemLabel="WhatsApp Form"
        retries={flowConfigsState.retries}
        retriesEnabled={retriesEnabled}
        onChange={handleRetriesChange}
      />
      <ConfigDivider />
      <ResponseWhatsappFailure
        actionProps={actionProps}
        apiProps={apiProps}
        campaignType={type}
        failure={flowConfigsState.onRetriesExhausted}
        id={id}
        info="Specify what we should do after the contact runs out of chances."
        partFamily={PartFamily.WhatsAppFlow}
        onChange={handleFailureChange}
      />
    </>
  )
}

export default React.memo(ResponseWhatsappFlowConfigs)

export type { Props as ResponseWhatsappFlowConfigsProps }
